import { Task } from '../../../types';

export interface ColumnCostSummary {
  [currency: string]: {
    estimated: number;
    actual: number;
  };
}

export interface ColumnTimeSummary {
  estimatedHours: number;
  actualHours: number;
  formattedEstimated: string;
  formattedActual: string;
}

/**
 * Calculates subtask completion percentage
 */
export const calculateTaskProgress = (subtasks: Task['subtasks']): number => {
  if (!subtasks || subtasks.length === 0) return 0;
  const completed = subtasks.filter((s) => s.completed).length;
  return Math.round((completed / subtasks.length) * 100);
};

/**
 * Returns the task's own time plus the time entered on all of its subtasks.
 */
export const getTaskTotalTime = (task: Task): { estimatedHours: number; actualHours: number } => {
  return (task.subtasks || []).reduce(
    (total, subtask) => ({
      estimatedHours: total.estimatedHours + (subtask.time?.estimatedHours || 0),
      actualHours: total.actualHours + (subtask.time?.actualHours || 0),
    }),
    {
      estimatedHours: task.time?.estimatedHours || 0,
      actualHours: task.time?.actualHours || 0,
    },
  );
};

/**
 * Returns the task's own cost plus the cost entered on its subtasks.
 * Subtask costs inherit the parent task currency, so they are only included
 * when the task has a currency-bearing cost object.
 */
export const getTaskTotalCost = (task: Task): { estimatedCost: number; actualCost: number } => {
  return (task.subtasks || []).reduce(
    (total, subtask) => ({
      estimatedCost: total.estimatedCost + (subtask.cost?.estimatedCost || 0),
      actualCost: total.actualCost + (subtask.cost?.actualCost || 0),
    }),
    {
      estimatedCost: task.cost?.estimatedCost || 0,
      actualCost: task.cost?.actualCost || 0,
    },
  );
};

/**
 * Groups costs by currency code and sums estimated vs actual amounts,
 * including subtask costs when the parent task has a currency.
 */
export const calculateColumnCosts = (tasks: Task[]): ColumnCostSummary => {
  return tasks.reduce((acc, task) => {
    if (!task.cost) return acc;
    const { currency } = task.cost;
    const totalCost = getTaskTotalCost(task);

    if (!acc[currency]) {
      acc[currency] = { estimated: 0, actual: 0 };
    }

    acc[currency].estimated += totalCost.estimatedCost;
    acc[currency].actual += totalCost.actualCost;

    return acc;
  }, {} as ColumnCostSummary);
};

/**
 * Converts total hours into readable "Xw Yd Zh" notation (40h week, 8h day).
 * Raw mode deliberately preserves the number rather than converting it.
 */
export const formatHours = (totalHours: number, raw: boolean = false): string => {
  if (!totalHours || totalHours <= 0) return '0h';
  if (raw) return `${totalHours}h`;

  const hoursPerDay = 8;
  const hoursPerWeek = 40;

  const weeks = Math.floor(totalHours / hoursPerWeek);
  const remainingAfterWeeks = totalHours % hoursPerWeek;

  const days = Math.floor(remainingAfterWeeks / hoursPerDay);
  const hours = remainingAfterWeeks % hoursPerDay;

  const parts: string[] = [];
  if (weeks > 0) parts.push(`${weeks}w`);
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || parts.length === 0) parts.push(`${hours}h`);

  return parts.join(' ');
};

/**
 * Sums all rolled-up task hours for a column.
 */
export const calculateColumnTime = (tasks: Task[], raw: boolean = false): ColumnTimeSummary => {
  const totals = tasks.reduce(
    (total, task) => {
      const taskTime = getTaskTotalTime(task);
      return {
        estimatedHours: total.estimatedHours + taskTime.estimatedHours,
        actualHours: total.actualHours + taskTime.actualHours,
      };
    },
    { estimatedHours: 0, actualHours: 0 },
  );

  return {
    ...totals,
    formattedEstimated: formatHours(totals.estimatedHours, raw),
    formattedActual: formatHours(totals.actualHours, raw),
  };
};
